/**
 * 羽数の妥当性判定。
 *
 * 羽数は小数を取り得ないため、画面のバリデーション・コンポーネントの保存前検証・
 * サービスの書き込み直前検証のすべてが本ファイルの関数を使う。
 * 判定を各層で個別に実装すると将来条件がずれるため、実装はここ1箇所に集約する。
 *
 * 2026-07 の飼養ロット1705 入荷羽数誤登録(20.841)の再発防止として追加。
 */

/** 羽数が整数でない場合に画面へ出すエラー文言 */
export const WASUU_INTEGER_ERROR_MESSAGE = '羽数は整数で入力してください。';

/** 保存対象に既存の不正な現在羽数が含まれる場合のエラー文言 */
export const EXISTING_GENZAI_WASUU_ERROR_MESSAGE = '入力値ではなく、既存データの現在羽数に整数でない値が含まれているため保存できません。管理者にデータ修正を依頼してください。';

/** 現在羽数が負数になる場合のエラー文言 */
export const NEGATIVE_GENZAI_WASUU_ERROR_MESSAGE = '現在羽数がマイナスになる値は登録できません。';

/** 羽数判定のオプション */
export interface WasuuValidationOptions {
  /** 負値を許容するか。訂正羽数のように減算の訂正がある項目で true にする */
  allowNegative?: boolean;
}

/**
 * 値が羽数として妥当か判定する
 *
 * NaN・Infinity・安全整数の範囲外・数値以外の型はすべて不正とする。
 * 未入力(null / undefined / 空文字)は required の責務のため、本関数では不正として扱う。
 * 未入力を許容したい呼び出し元は、本関数を呼ぶ前に自分で除外すること。
 *
 * @param value 判定対象の値
 * @param options 判定オプション
 * @returns {boolean} 羽数として妥当なら true
 */
export function isValidWasuu(value: unknown, options: WasuuValidationOptions = {}): boolean {
  if (!Number.isSafeInteger(value)) {
    return false;
  }
  return options.allowNegative ? true : (value as number) >= 0;
}

/**
 * オブジェクトの複数の羽数プロパティを検証し、最初に見つかった不正なキー名を返す
 *
 * @param source 検証対象のオブジェクト
 * @param keys 検証する羽数プロパティのキー
 * @param allowNegativeKeys 負値を許容するキー(訂正羽数など)
 * @returns {string | null} 不正なキー名。すべて妥当なら null
 */
export function findInvalidWasuuKey<T extends Record<string, any>>(
  source: T,
  keys: readonly (keyof T & string)[],
  allowNegativeKeys: readonly string[] = []
): string | null {
  for (const key of keys) {
    if (!isValidWasuu(source[key], {allowNegative: allowNegativeKeys.includes(key)})) {
      return key;
    }
  }
  return null;
}

/**
 * 既存の現在羽数と差分反映後の現在羽数を検証する
 *
 * 既存値を先に検証し、今回の入力が原因ではない不正を専用メッセージで通知する。
 *
 * @param existingValues 保存前から存在する現在羽数
 * @param updatedValues 今回の差分を反映した後の現在羽数
 * @returns {string | null} 不正時のメッセージ。すべて妥当なら null
 */
export function findGenzaiWasuuError(
  existingValues: readonly unknown[],
  updatedValues: readonly unknown[]
): string | null {
  if (existingValues.some(value => !isValidWasuu(value))) {
    return EXISTING_GENZAI_WASUU_ERROR_MESSAGE;
  }
  if (updatedValues.some(value => typeof value === 'number' && value < 0)) {
    return NEGATIVE_GENZAI_WASUU_ERROR_MESSAGE;
  }
  if (updatedValues.some(value => !isValidWasuu(value))) {
    return WASUU_INTEGER_ERROR_MESSAGE;
  }
  return null;
}

/** 育成記録の羽数検証に必要な最小構造 */
export interface IkuseiKirokuWasuuSource {
  shukkaWasuu: unknown;
  header: {
    nyuukaWasuu: unknown;
  };
  weeklyMap: Record<string, {
    shuumatsuWasuu?: unknown;
  }>;
}

/**
 * 客先提出用育成記録に不正な羽数が含まれるか判定する
 *
 * 出荷羽数と入荷羽数は必須として検証する。
 * 週末羽数は全週を検証するが、未入力値は許容する。
 *
 * @param source 検証対象の育成記録
 * @returns {boolean} 不正な羽数が含まれる場合は true
 */
export function hasInvalidIkuseiKirokuWasuu(
  source: IkuseiKirokuWasuuSource
): boolean {
  if (!isValidWasuu(source.shukkaWasuu) || !isValidWasuu(source.header.nyuukaWasuu)) {
    return true;
  }

  return Object.keys(source.weeklyMap).some(key => {
    const value = source.weeklyMap[key].shuumatsuWasuu;
    const isEmpty = value === undefined || value === null || value === '';
    return !isEmpty && !isValidWasuu(value);
  });
}
